import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Hls from 'hls.js';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';

import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded';
import { Select, SelectTypes } from '@nhl/nhl-breakaway-ui';

import { getDataLayer } from '~/core/googleAnalyticsUtils';
import { parseValueFromLocalizedAttribute } from '~/core/utils';

import { GameCenterTeam } from '../../../../types';

import {
    PickerLabel,
    RadioPlayerCloseButton,
    RadioPlayerContainer,
    RadioPlayerPicker,
    RadioPlayerPlayButton,
} from './styles';

const M3U8_FILE_DESCRIPTOR = 'application/vnd.apple.mpegurl';
const CAN_PLAY_THROUGH_EVENT = 'canplaythrough';

const radioSelectLabel = defineMessage({
    description: 'A string which denotes a team\'s radio streaming link, eg "Wild Radio".',
    defaultMessage: '{team} Radio',
});

const radioSelectLabelFrench = defineMessage({
    description:
        'A string which denotes a team\'s French radio streaming link, eg "Wild Radio (FR)". Note that FR is the abbreviation for French and should be translated.',
    defaultMessage: '{team} Radio (FR)',
});

type RadioPlayerProps = {
    homeTeam: GameCenterTeam;
    awayTeam: GameCenterTeam;
    closeRadioPlayer: () => void;
};

const RadioPlayer: FC<RadioPlayerProps> = ({ homeTeam, awayTeam, closeRadioPlayer }) => {
    const intl = useIntl();

    const radioOptions: Array<SelectTypes.BasicOption> = useMemo(() => {
        const options: Array<SelectTypes.BasicOption> = [];

        if (homeTeam.radioLink) {
            options.push({
                label: intl.formatMessage(radioSelectLabel, {
                    team: parseValueFromLocalizedAttribute(homeTeam.commonName),
                }),
                value: homeTeam.radioLink,
                key: 'home-english',
            });
        }

        if (homeTeam.radioLinkFr) {
            options.push({
                label: intl.formatMessage(radioSelectLabelFrench, {
                    team: parseValueFromLocalizedAttribute(homeTeam.commonName),
                }),
                value: homeTeam.radioLinkFr,
                key: 'home-french',
            });
        }

        if (awayTeam.radioLink) {
            options.push({
                label: intl.formatMessage(radioSelectLabel, {
                    team: parseValueFromLocalizedAttribute(awayTeam.commonName),
                }),
                value: awayTeam.radioLink,
                key: 'away-english',
            });
        }

        if (awayTeam.radioLinkFr) {
            options.push({
                label: intl.formatMessage(radioSelectLabelFrench, {
                    team: parseValueFromLocalizedAttribute(awayTeam.commonName),
                }),
                value: awayTeam.radioLinkFr,
                key: 'away-french',
            });
        }

        return options;
    }, [awayTeam, homeTeam, intl]);

    const [selectedStream, setSelectedStream] = useState<SelectTypes.BasicOption>(radioOptions[0]);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const playerRef = useRef<HTMLAudioElement>(new Audio());

    const restartPlayback = useCallback(() => {
        if (isPlaying) {
            playerRef.current.play();
        }
    }, [isPlaying]);

    useEffect(() => {
        const playerEl = playerRef.current;
        let hlsInstance: Hls;

        if (playerEl.canPlayType(M3U8_FILE_DESCRIPTOR)) {
            playerEl.src = selectedStream.value as string;
            playerEl.load();
        } else if (Hls.isSupported()) {
            hlsInstance = new Hls();

            hlsInstance.loadSource(selectedStream.value as string);
            hlsInstance.attachMedia(playerRef.current);
        }

        return () => {
            playerEl.src = '';
            playerEl.pause();
            hlsInstance?.detachMedia();
            hlsInstance?.destroy();
        };
    }, [selectedStream]);

    useEffect(() => {
        const playerEl = playerRef.current;

        // basically if the user switches radio streams we want to restart playback when possible
        // this event listener will only cause playing to occur when it already was happening
        playerEl.addEventListener(CAN_PLAY_THROUGH_EVENT, restartPlayback);

        return () => {
            playerEl.removeEventListener(CAN_PLAY_THROUGH_EVENT, restartPlayback);
        };
    }, [restartPlayback]);

    const onClickRadioPlayButton = () => {
        const playerEl = playerRef.current;

        if (isPlaying) {
            playerEl.pause();
            setIsPlaying(false);
        } else {
            getDataLayer().push({
                event: 'audio_start',
                audio_feed: selectedStream.key,
                teams_playing: `${homeTeam.abbrev},${awayTeam.abbrev}`,
            });

            playerEl.play();
            setIsPlaying(true);
        }
    };

    return (
        <RadioPlayerContainer>
            <RadioPlayerPlayButton onClick={onClickRadioPlayButton}>
                {isPlaying ? <StopCircleRoundedIcon /> : <PlayCircleFilledRoundedIcon />}
            </RadioPlayerPlayButton>
            <RadioPlayerPicker>
                <PickerLabel htmlFor="radio-stream-select">
                    <FormattedMessage
                        description="Indicates that the user can select a radio stream to play."
                        defaultMessage="Select Feed"
                    />
                </PickerLabel>
                <Select
                    dropdownButtonAriaLabel={intl.formatMessage({
                        defaultMessage: 'Expand Radio Stream Options',
                        description:
                            'Label for select element button, prompting user to expand radio stream options in a dropdown menu',
                    })}
                    id="radio-stream-select"
                    onOptionChange={(e, value: SelectTypes.BasicOption) => setSelectedStream(value)}
                    options={radioOptions}
                    optionsBoxAriaLabel={intl.formatMessage({
                        defaultMessage: 'Radio Stream Options',
                        description:
                            'Label indicating that user is viewing a list of radio stream options for a select component',
                    })}
                    selectedOption={selectedStream || null}
                />
            </RadioPlayerPicker>
            <RadioPlayerCloseButton>
                <CancelRoundedIcon onClick={closeRadioPlayer} />
            </RadioPlayerCloseButton>
        </RadioPlayerContainer>
    );
};

export default RadioPlayer;
